// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid2,
  Typography,
} from '@mui/material';
// Components, Layouts, Pages
import { BaseLabelValueField, BaseStatus, Loading } from '~/components/common';
// Others
import { getEmployeeDetail } from '~/thunks/employee/employeeThunk';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IEmployeeDetail } from '~/utils/interface/employee';
import { EMPTY_STRING } from '~/utils/constants/common';
import { convertCodeToRole, formatPhoneNumber, getFullName, getInitials } from '~/utils/helper/common';
// Styles, images, icons
import styles from './EmployeeDetailModal.module.scss';

type Props = {
  employeeId: string;
  isOpen: boolean;
  onClose: () => void;
};

const cx = classNames.bind(styles);

const EmployeeDetailModal = (props: Props) => {
  //#region Destructuring Props
  const { employeeId, isOpen, onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/employee', 'common/base']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [scroll] = useState<DialogProps['scroll']>('paper');
  const [employeeDetail, setEmployeeDetail] = useState<IEmployeeDetail>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!employeeId) return;

    handleGetEmployeeDetail(employeeId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetEmployeeDetail = (employeeId: string) => {
    setIsLoading(true);

    dispatch(getEmployeeDetail(employeeId))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setEmployeeDetail(res?.data);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleCloseEmployeeDetail = () => {
    onClose();
    setEmployeeDetail({} as IEmployeeDetail);
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='employeeDetailModalComponent'
      open={isOpen}
      onClose={handleCloseEmployeeDetail}
      fullWidth
      maxWidth={'sm'}
    >
      <DialogTitle>{t('employee_detail_title')}</DialogTitle>

      <DialogContent dividers={scroll === 'paper'}>
        <Grid2 container spacing={'24px'}>
          <Grid2 size={8}>
            <BaseLabelValueField
              label={t('employee_detail_name')}
              value={getFullName({
                firstName: employeeDetail?.firstName,
                lastName: employeeDetail?.lastName,
              })}
            />

            <BaseLabelValueField
              label={t('employee_detail_email')}
              value={employeeDetail?.email || EMPTY_STRING}
            />

            <BaseLabelValueField
              label={t('employee_detail_phone')}
              value={formatPhoneNumber(employeeDetail?.phoneNumber) || EMPTY_STRING}
            />

            {employeeDetail?.role && (
              <BaseLabelValueField
                label={t('employee_detail_position')}
                value={
                  <BaseStatus
                    label={t(convertCodeToRole(employeeDetail?.role || EMPTY_STRING), { ns: 'common/base' })}
                  />
                }
              />
            )}
          </Grid2>

          <Grid2 size={4} className={cx('avatarContainer')}>
            {employeeDetail?.avatarImage && (
              <Avatar
                src={employeeDetail?.avatarImage}
                alt={t('common_alt_img')}
                className={cx('avatarImage')}
              />
            )}

            {!employeeDetail?.avatarImage && employeeDetail?.firstName && employeeDetail?.lastName && (
              <Avatar
                src={employeeDetail?.avatarImage}
                alt={t('common_alt_img')}
                className={cx('avatarImage')}
              >
                <Typography>
                  {getInitials({
                    firstName: employeeDetail?.firstName,
                    lastName: employeeDetail?.lastName,
                  })}
                </Typography>
              </Avatar>
            )}
          </Grid2>
        </Grid2>
      </DialogContent>

      <DialogActions>
        <Button variant='outlined' onClick={handleCloseEmployeeDetail}>
          {t('common_action_cancel', { ns: 'common/base' })}
        </Button>
      </DialogActions>

      {isLoading && <Loading />}
    </Dialog>
  );
};

export default EmployeeDetailModal;
